import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LifeBuoy } from "lucide-react";
import { useAuth } from "../../context/authContext";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import portalStyles from "../../layout/portalPageShell.module.css";
import styles from "./SupportPage.module.css";
import Button from "../../components/UI/Button/Button";

function accountDisplayName(user) {
  if (!user) return "";
  const fromNames = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  if (fromNames) return fromNames;
  return (user.name || "").trim();
}

export default function SupportPage() {
  const { t } = useTranslation("common");
  const { user } = useAuth();

  const accountEmail = user?.email?.trim() || "";
  const accountName = accountDisplayName(user);
  const [activeTab, setActiveTab] = useState("contact");
  const [contactForm, setContactForm] = useState({
    subject: "",
    message: "",
    priority: "medium",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setContactForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    if (!accountEmail) {
      alert(t("supportPage.alerts.noAccountEmail"));
      return;
    }
    if (!accountName) {
      alert(t("supportPage.alerts.noAccountName"));
      return;
    }
    setIsSubmitting(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      alert(t("supportPage.alerts.submitted"));
      setContactForm({
        subject: "",
        message: "",
        priority: "medium",
      });
    } catch {
      alert(t("supportPage.alerts.failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqItems = t("supportPage.faq.items", { returnObjects: true }) || [];

  return (
    <div className={portalStyles.portalPage}>
      <div className={portalStyles.container}>
        <PortalShellPageHeader
          icon={<LifeBuoy size={28} strokeWidth={1.75} absoluteStrokeWidth />}
          title={t("supportPage.title")}
          subtitle={t("supportPage.subtitle")}
        />

        <div className={styles.supportMain}>
          <div className={styles.tabNavigation}>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "contact" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("contact")}
            >
              {t("supportPage.tabs.contact")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "faq" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("faq")}
            >
              {t("supportPage.tabs.faq")}
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "resources" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("resources")}
            >
              {t("supportPage.tabs.resources")}
            </button>
          </div>

          {activeTab === "contact" && (
            <div className={styles.tabContent}>
              <div className={styles.contactSection}>
                <div className={styles.contactInfo}>
                  <h2>{t("supportPage.contact.title")}</h2>
                  <p>{t("supportPage.contact.subtitle")}</p>

                  <div className={styles.contactMethods}>
                    <div className={styles.contactMethod}>
                      <div className={styles.methodIcon} aria-hidden="true">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </div>
                      <div className={styles.methodInfo}>
                        <h3>{t("supportPage.contact.methods.email.label")}</h3>
                        <p>support@softindex.ro</p>
                      </div>
                    </div>
                    <div className={styles.contactMethod}>
                      <div className={styles.methodIcon} aria-hidden="true">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </div>
                      <div className={styles.methodInfo}>
                        <h3>{t("supportPage.contact.methods.phone.label")}</h3>
                        <p>+40 757 217 748</p>
                      </div>
                    </div>
                    <div className={styles.contactMethod}>
                      <div className={styles.methodIcon} aria-hidden="true">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                      </div>
                      <div className={styles.methodInfo}>
                        <h3>{t("supportPage.contact.methods.hours.label")}</h3>
                        <p>{t("supportPage.contact.methods.hours.value")}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <form
                  onSubmit={handleSubmitContact}
                  className={styles.contactForm}
                >
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label
                        className={styles.formLabel}
                        htmlFor="support-account-name"
                      >
                        {t("supportPage.form.accountName")}
                      </label>
                      <input
                        id="support-account-name"
                        type="text"
                        className={`${styles.formInput} ${styles.formInputReadonly}`}
                        value={accountName}
                        readOnly
                        autoComplete="off"
                        tabIndex={-1}
                        aria-describedby="support-account-name-hint"
                      />
                      <p
                        id="support-account-name-hint"
                        className={styles.formFieldHint}
                      >
                        {t("supportPage.form.accountNameHint")}
                      </p>
                    </div>
                    <div className={styles.formGroup}>
                      <label
                        className={styles.formLabel}
                        htmlFor="support-account-email"
                      >
                        {t("supportPage.form.accountEmail")}
                      </label>
                      <input
                        id="support-account-email"
                        type="email"
                        className={`${styles.formInput} ${styles.formInputReadonly}`}
                        value={accountEmail}
                        readOnly
                        autoComplete="off"
                        tabIndex={-1}
                        aria-describedby="support-account-email-hint"
                      />
                      <p
                        id="support-account-email-hint"
                        className={styles.formFieldHint}
                      >
                        {t("supportPage.form.accountEmailHint")}
                      </p>
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        {t("supportPage.form.subject")}
                      </label>
                      <input
                        type="text"
                        className={styles.formInput}
                        value={contactForm.subject}
                        onChange={(e) =>
                          handleInputChange("subject", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        {t("supportPage.form.priority")}
                      </label>
                      <select
                        className={styles.formSelect}
                        value={contactForm.priority}
                        onChange={(e) =>
                          handleInputChange("priority", e.target.value)
                        }
                      >
                        <option value="low">
                          {t("supportPage.form.priorities.low")}
                        </option>
                        <option value="medium">
                          {t("supportPage.form.priorities.medium")}
                        </option>
                        <option value="high">
                          {t("supportPage.form.priorities.high")}
                        </option>
                        <option value="urgent">
                          {t("supportPage.form.priorities.urgent")}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      {t("supportPage.form.message")}
                    </label>
                    <textarea
                      className={styles.formTextarea}
                      rows="6"
                      value={contactForm.message}
                      onChange={(e) =>
                        handleInputChange("message", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className={styles.formSubmit}>
                    <Button
                      type="submit"
                      disabled={
                        isSubmitting || !accountEmail || !accountName
                      }
                    >
                      {isSubmitting
                        ? t("supportPage.form.submitting")
                        : t("supportPage.form.submit")}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === "faq" && (
            <div className={styles.tabContent}>
              <div className={styles.faqSection}>
                <h2>{t("supportPage.faq.title")}</h2>
                <div className={styles.faqList}>
                  {Array.isArray(faqItems) &&
                    faqItems.map((item, index) => (
                      <div key={index} className={styles.faqItem}>
                        <h3 className={styles.faqQuestion}>{item.question}</h3>
                        <p className={styles.faqAnswer}>{item.answer}</p>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "resources" && (
            <div className={styles.tabContent}>
              <div className={styles.resourcesSection}>
                <h2>{t("supportPage.resources.title")}</h2>
                <div className={styles.resourcesGrid}>
                  <div className={styles.resourceCard}>
                    <div className={styles.resourceIcon} aria-hidden="true">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M12 19a3 3 0 0 0-3-3H4V4h5a3 3 0 0 1 3 3z" />
                        <path d="M12 19a3 3 0 0 1 3-3h5V4h-5a3 3 0 0 0-3 3z" />
                      </svg>
                    </div>
                    <h3>{t("supportPage.resources.cards.userGuide.title")}</h3>
                    <p>
                      {t("supportPage.resources.cards.userGuide.description")}
                    </p>
                    <a
                      href="https://www.softindex.ro/contact"
                      className={styles.resourceLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("supportPage.resources.cards.userGuide.link")} →
                    </a>
                  </div>
                  <div className={styles.resourceCard}>
                    <div className={styles.resourceIcon} aria-hidden="true">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="2" y="7" width="15" height="10" rx="2" ry="2" />
                        <polygon points="23 7 18 10 18 14 23 17 23 7" />
                      </svg>
                    </div>
                    <h3>
                      {t("supportPage.resources.cards.videoTutorials.title")}
                    </h3>
                    <p>
                      {t(
                        "supportPage.resources.cards.videoTutorials.description"
                      )}
                    </p>
                    <a
                      href="https://www.softindex.ro/contact"
                      className={styles.resourceLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("supportPage.resources.cards.videoTutorials.link")} →
                    </a>
                  </div>
                  <div className={styles.resourceCard}>
                    <div className={styles.resourceIcon} aria-hidden="true">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.1-3.1a6 6 0 0 1-7.6 7.6l-7.9 7.9a2 2 0 0 1-2.8-2.8l7.9-7.9a6 6 0 0 1 7.6-7.6z" />
                      </svg>
                    </div>
                    <h3>{t("supportPage.resources.cards.apiDocs.title")}</h3>
                    <p>{t("supportPage.resources.cards.apiDocs.description")}</p>
                    <a
                      href="https://www.softindex.ro/contact"
                      className={styles.resourceLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("supportPage.resources.cards.apiDocs.link")} →
                    </a>
                  </div>
                  <div className={styles.resourceCard}>
                    <div className={styles.resourceIcon} aria-hidden="true">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                      </svg>
                    </div>
                    <h3>{t("supportPage.resources.cards.community.title")}</h3>
                    <p>
                      {t("supportPage.resources.cards.community.description")}
                    </p>
                    <a
                      href="mailto:support@softindex.ro"
                      className={styles.resourceLink}
                    >
                      {t("supportPage.resources.cards.community.link")} →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
